import "swiper/swiper.scss"; // core Swiper
import "swiper/modules/navigation/navigation.scss"; // Navigation module
import "swiper/modules/pagination/pagination.scss"; // Pagination module
import "swiper/modules/autoplay/autoplay.scss"; // Autoplay module
import { useMemo } from "react";
import { FlatList, View } from "react-native";
import { routeAggregatePage, routeVamAggregatePage } from "../../Routes";
import CardPackshot from "../CardPackshot";
import SeeMoreComponent from "../SeeMoreComponent";
import { useNavigation } from "@react-navigation/native";
import useGenericContentStore from "../../store/genericContent.store";
import { DraggableScrollView } from "../DraggableScroll";
import { Props } from "./model";
import { StyledSlider } from "./styled";

const PlaylistSlider = (props: Props) => {
	const navigation = useNavigation();
	const setAggregateTitle = useGenericContentStore((state: any) => state.setAggregateTitle);
	const { isNarrow } = useGenericContentStore((state: any) => ({ isNarrow: state.isNarrow }));

	/**
	 * Handles the "See More" button press for a playlist or aggregate page.
	 * - Sets the aggregate title to the current props title.
	 * - Navigates to the VAM aggregate page if in studio access mode.
	 * - Otherwise, navigates to the standard aggregate page with location and ID.
	 *
	 * @param id - The ID of the playlist or custom list to navigate to.
	 */
	const onPressSeeMore = (id: any) => {
		setAggregateTitle(props.title);

		if (props.isStudioAccess) {
			//@ts-ignore
			navigation.navigate(routeVamAggregatePage, { customListId: id });
			return;
		}
		//@ts-ignore
		navigation.navigate(routeAggregatePage, { location: props.location, id });
	};

	/**
	 * Renders a single item in the Swiper slider.
	 * - Wraps the item in a styled slider.
	 * - Conditionally uses `customListId` to determine which CardPackshot props to pass.
	 *
	 * @param item - The item object containing product details.
	 * @param index - Index of the item in the slider.
	 * @returns JSX.Element - The rendered slider item.
	 */
	const renderItem = ({ item, index }: any) => {
		const isCustomList = !!props?.customListId;

		const commonProps = {
			key: isCustomList ? `short-clips-${item?.productId}` : `products-${item?.parentProductId}`,
			isLandscape: props.isLandscape,
			isStudioAccess: props.isStudioAccess,
			isProgressBar: props.isProgressBar,
			streamProgress: item?.streamProgress,
			isPreview: props?.isPreview,
			isHome: props?.isHome,
		};

		const specificProps = isCustomList
			? {
					productId: item?.productId,
					title: item?.title,
					image: item?.imageUrl,
					vamId: item?.uniqueContentId,
					isAssignToScreen: props.isAssignToScreen,
					customListId: props.customListId,
				}
			: {
					productId: item?.parentProductId,
					image: item?.packshots,
					backupImage: item?.backupPackshot,
				};

		return (
			//@ts-ignore
			<StyledSlider
				key={`swiper-slide-${item?.parentProductId ?? item?.id}`}
				isLandscape={props.isLandscape}
				isBackUpImage={!item?.packshots && !item?.uniqueContentId}
				isStudioAccess={props.isStudioAccess}
				index={index}
				isNarrow={isNarrow}
				isPreview={props.isPreview}
			>
				<CardPackshot {...commonProps} {...specificProps} />
			</StyledSlider>
		);
	};

	/**
	 * Renders the "See More" button for a playlist if applicable.
	 * - Only shows when `props.isSeeMore` is true and the number of products exceeds `props.seeMoreLength`.
	 * - Wraps the `SeeMoreComponent` inside a styled slider with layout adjustments for landscape, studio access, and narrow screens.
	 * - Handles the press action via `onPressSeeMore`.
	 */
	const renderSeeMore = (
		<>
			{props.isSeeMore && props?.products?.length >= props.seeMoreLength ? (
				//@ts-ignore
				<StyledSlider key={`see-more-${props.id}`} isLandscape={props.isLandscape} isStudioAccess={props.isStudioAccess} isNarrow={isNarrow}>
					<SeeMoreComponent
						key={`see-more-button-${props.id}`}
						onPress={() => onPressSeeMore(props.id)}
						size={14}
						isLandscape={props.isLandscape}
						isNarrow={isNarrow}
						isClip={props.isStudioAccess}
					/>
				</StyledSlider>
			) : (
				<></>
			)}
		</>
	);

	/**
	 * Memoized FlatList of playlist items wrapped in a draggable scroll view.
	 * - Renders horizontally with configurable batching and window size based on landscape or studio access.
	 * - Uses `renderItem` for each playlist item.
	 * - Shows a footer component via `renderSeeMore`.
	 * - Optimizes rendering by adjusting `initialNumToRender`, `maxToRenderPerBatch`, `updateCellsBatchingPeriod`, and `windowSize`.
	 *
	 * Dependencies:
	 * - Recomputes only when `props.products`, `props.isLandscape`, `props.isStudioAccess`, `props.isAssignToScreen`, or `isNarrow` change.
	 */
	const renderPlaylist = useMemo(
		() => (
			//@ts-ignore
			<DraggableScrollView>
				<FlatList
					data={props?.products}
					renderItem={renderItem}
					horizontal={true}
					showsVerticalScrollIndicator={false}
					showsHorizontalScrollIndicator={false}
					removeClippedSubviews={true}
					initialNumToRender={props.isLandscape || props.isStudioAccess ? 3 : 4} // Reduce initial render amount
					maxToRenderPerBatch={props.isLandscape || props.isStudioAccess ? 6 : 8}
					updateCellsBatchingPeriod={props.isLandscape || props.isStudioAccess ? 2 : 3}
					windowSize={props.isLandscape || props.isStudioAccess ? 2 : 3}
					ListFooterComponent={renderSeeMore}
					keyExtractor={(item) => item?.parentProductId ?? item?.id}
				/>
			</DraggableScrollView>
		),
		[props?.products, props.isLandscape, props.isStudioAccess, props.isAssignToScreen, isNarrow],
	);

	return <View style={{ width: "100%" }}>{renderPlaylist}</View>;
};

export default PlaylistSlider;
